// Sidebar.jsx
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Package, LogOut, Menu, X, Gift, UserCheck, Building2, AlertTriangle, MapPin, Users, Wallet, LayoutDashboard, ShieldAlert, Sparkles } from 'lucide-react';
import { logout } from '../../services/authService';
import Logo from '../ui/Logo';

const NAV_GROUPS = [
  {
    label: 'Overview',
    items: [
      { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/admin/dashboard' }
    ]
  },
  {
    label: 'Bookings & Orders',
    items: [
      { id: 'marketplace-orders', label: 'Marketplace orders', icon: Package, path: '/admin/marketplace-orders' }
    ]
  },
  {
    label: 'Verification',
    items: [
      { id: 'therapist-verification', label: 'Therapists', icon: UserCheck, path: '/admin/therapist-verification' },
      { id: 'venue-verification', label: 'Venues', icon: Building2, path: '/admin/venue-verification' }
    ]
  },
  {
    label: 'Safety',
    items: [
      { id: 'disputes', label: 'Disputes', icon: AlertTriangle, path: '/admin/disputes' },
      { id: 'flagged-reports', label: 'Safety reports', icon: ShieldAlert, path: '/admin/flagged-reports' }
    ]
  },
  {
    label: 'Catalog',
    items: [
      { id: 'zones', label: 'Zones', icon: MapPin, path: '/admin/zones' },
      { id: 'services', label: 'Services', icon: Sparkles, path: '/admin/services' },
      { id: 'coupons', label: 'Coupons', icon: Gift, path: '/admin/coupons' }
    ]
  },
  {
    label: 'People & Finance',
    items: [
      { id: 'users', label: 'Users', icon: Users, path: '/admin/users' },
      { id: 'withdrawals', label: 'Withdrawals', icon: Wallet, path: '/admin/withdrawals' }
    ]
  }
];

const Sidebar = ({ activeTab, setActiveTab, isOpen, setIsOpen, isMobile }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      window.location.reload();
    }
  };

  const NavButton = ({ item }) => {
    const active = location.pathname === item.path;
    return (
      <button
        onClick={() => {
          setActiveTab(item.id);
          navigate(item.path);
        }}
        className={`flex items-center w-full py-2 px-3 rounded-lg text-sm transition-colors ${
          active ? 'bg-accent-hard text-primary-dark font-semibold' : 'text-secondary/80 hover:bg-primary-light/40 hover:text-secondary'
        } ${!isOpen ? 'justify-center' : ''}`}
        title={!isOpen ? item.label : ''}
      >
        <item.icon className={isOpen ? 'mr-3 shrink-0' : 'shrink-0'} size={20} />
        {isOpen && <span className="truncate">{item.label}</span>}
      </button>
    );
  };

  return (
    <div className={`bg-primary-dark h-screen fixed left-0 top-0 z-50 transition-all duration-300 flex flex-col ${
      isOpen ? 'w-64' : 'w-16'
    } ${isMobile && !isOpen ? '-translate-x-full' : 'translate-x-0'}`}>
      {/* Desktop-only collapse toggle — mobile uses its own top-bar hamburger,
          so this would otherwise show as a confusing second icon on small screens. */}
      {!isMobile && (
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="absolute right-0 top-6 translate-x-1/2 bg-primary-dark border border-primary-light/30 rounded-full p-1.5 hover:bg-primary-light z-50"
        >
          {isOpen ? (
            <X className="text-secondary" size={18} />
          ) : (
            <Menu className="text-secondary" size={18} />
          )}
        </button>
      )}

      <div className={`px-4 pt-6 pb-4 border-b border-primary-light/20 ${!isOpen ? 'flex justify-center' : ''}`}>
        {isOpen ? <Logo variant="light" /> : <span className="text-accent-light font-heading text-xl font-bold">S</span>}
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
        {NAV_GROUPS.map((group) => (
          <div key={group.label}>
            {isOpen && (
              <p className="px-3 mb-1.5 text-[11px] uppercase tracking-wider text-secondary/40 font-semibold">
                {group.label}
              </p>
            )}
            <div className="space-y-1">
              {group.items.map((item) => <NavButton key={item.id} item={item} />)}
            </div>
          </div>
        ))}
      </nav>

      <div className="p-3 border-t border-primary-light/20">
        <button
          onClick={handleLogout}
          className={`flex items-center w-full py-2 px-3 rounded-lg text-sm text-secondary/80 hover:bg-red-900/40 hover:text-red-200 ${
            !isOpen ? 'justify-center' : ''
          }`}
          title={!isOpen ? 'Logout' : ''}
        >
          <LogOut className={isOpen ? 'mr-3 shrink-0' : 'shrink-0'} size={20} />
          {isOpen && 'Logout'}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
